import { create } from 'zustand';
import type {
  DashboardStatus,
  Portfolio,
  Position,
  Signal,
  Strategy,
  RiskParams,
  RiskExposure,
  WatchlistItem,
  PerformanceData,
} from '../types';

interface TradingState {
  // Connection
  connected: boolean;
  lastUpdate: string | null;

  // Core data
  status: DashboardStatus | null;
  portfolio: Portfolio | null;
  positions: Position[];
  signals: Signal[];
  strategies: Strategy[];
  watchlist: WatchlistItem[];

  // Risk
  riskParams: RiskParams | null;
  riskExposure: RiskExposure | null;

  // Performance
  performance: PerformanceData | null;

  // UI state
  selectedSymbol: string | null;
  isKillSwitchModalOpen: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setConnected: (connected: boolean) => void;
  setStatus: (status: DashboardStatus) => void;
  setPortfolio: (portfolio: Portfolio) => void;
  setPositions: (positions: Position[]) => void;
  addSignal: (signal: Signal) => void;
  setSignals: (signals: Signal[]) => void;
  setStrategies: (strategies: Strategy[]) => void;
  setWatchlist: (watchlist: WatchlistItem[]) => void;
  setRiskParams: (params: RiskParams) => void;
  setRiskExposure: (exposure: RiskExposure) => void;
  setPerformance: (performance: PerformanceData) => void;
  setSelectedSymbol: (symbol: string | null) => void;
  setKillSwitchModalOpen: (open: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  updateFromWebSocket: (data: {
    status?: DashboardStatus;
    portfolio?: Portfolio;
    positions?: Position[];
    recent_signals?: Signal[];
    new_signals?: Signal[];
  }) => void;
}

export const useTradingStore = create<TradingState>((set) => ({
  // Initial state
  connected: false,
  lastUpdate: null,
  status: null,
  portfolio: null,
  positions: [],
  signals: [],
  strategies: [],
  watchlist: [],
  riskParams: null,
  riskExposure: null,
  performance: null,
  selectedSymbol: null,
  isKillSwitchModalOpen: false,
  isLoading: true,
  error: null,

  // Actions
  setConnected: (connected) => set({ connected }),

  setStatus: (status) => set({ status, lastUpdate: new Date().toISOString() }),

  setPortfolio: (portfolio) => set({ portfolio }),

  setPositions: (positions) => set({ positions }),

  addSignal: (signal) =>
    set((state) => ({
      signals: [signal, ...state.signals.slice(0, 99)],
    })),

  setSignals: (signals) => set({ signals }),

  setStrategies: (strategies) => set({ strategies }),

  setWatchlist: (watchlist) => set({ watchlist }),

  setRiskParams: (riskParams) => set({ riskParams }),

  setRiskExposure: (riskExposure) => set({ riskExposure }),

  setPerformance: (performance) => set({ performance }),

  setSelectedSymbol: (selectedSymbol) => set({ selectedSymbol }),

  setKillSwitchModalOpen: (isKillSwitchModalOpen) => set({ isKillSwitchModalOpen }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  updateFromWebSocket: (data) =>
    set((state) => {
      // Merge new signals with existing, avoiding duplicates
      let updatedSignals = state.signals;

      if (data.new_signals && data.new_signals.length > 0) {
        const existingIds = new Set(state.signals.map(s => s.id));
        const newUnique = data.new_signals.filter(s => !existingIds.has(s.id));
        updatedSignals = [...newUnique, ...state.signals].slice(0, 100);
      } else if (data.recent_signals && state.signals.length < 100) {
        // Initialize signals on first load (or if WebSocket only delivered partial)
        updatedSignals = data.recent_signals;
      }

      return {
        ...(data.status && { status: data.status }),
        ...(data.portfolio && { portfolio: data.portfolio }),
        ...(data.positions && { positions: data.positions }),
        signals: updatedSignals,
        lastUpdate: new Date().toISOString(),
        connected: true,
      };
    }),
}));
